'use client';

// Single document row for the Documents tab workbench table (design Section
// 6.1). Migrates DocSlot's upload/delete/quality-check logic (doUpload,
// handleDelete, handleFile, runQualityCheck) unchanged from
// ClaimDocumentsTab.tsx — same fail-open quality-check posture, same API
// calls — restyled into a compact table row with the quality warning
// rendered as an expandable sub-row (design Section 6.6), not a popover.

import { useRef, useState } from 'react';
import { cn } from '@/lib/utils';
import type { DocDef, DocRecord, ExtractionSummary } from './types';
import type { RequestContext } from '../media/requestContext';

const API_URL = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:8000';
const DOCS_BASE = (jobId: string) =>
  `${API_URL}/api/v1/insurance/vehicle-claims/${jobId}/documents`;
// QUALITY_CHECK_URL removed -- the quality-check call is disabled below (billing reduction).
const HTTP_DELETE = 'DELETE';

// Extraction-capable doc types — the Documents tab only offers the OCR
// Extraction UI (Review button, Extracted/Processing/Failed status badge)
// for these. Mirrors EXTRACTION_DOC_TYPES in hooks/useDocumentExtractions.ts
// and the invoice/estimate + DocOCRModal eligibility gates in
// ClaimDocumentsTab.tsx — keep all three in sync.
const EXTRACTION_DOC_TYPES = new Set([
  'repair_invoice_1', 'repair_invoice_2', 'repair_invoice_3',
  'dl_front', 'dl_back',
  'rc_front', 'rc_back',
  'policy_copy', 'claims_form',
]);

interface QualityResult {
  quality: 'good' | 'acceptable' | 'poor';
  score: number;
  ocr_viable: boolean;
  issues: string[];
  suggestion: string;
}

function authHeaders(): Record<string, string> {
  const token = localStorage.getItem('access_token') || localStorage.getItem('super_admin_token') || '';
  const tenantId = localStorage.getItem('tenant_id') || '';
  const userType = localStorage.getItem('user_type') || '';
  return {
    Authorization: `Bearer ${token}`,
    'X-Tenant-Id': tenantId,
    'X-User-Type': userType,
  };
}

// Thrown when the refresh request itself couldn't be completed (network
// error, timeout -- no response from the server at all), as opposed to a
// plain `null` return, which means the server was reached and explicitly
// rejected the refresh token. Only the latter means the session is
// genuinely over. This matters most right when a laptop wakes from
// sleep/lock: that's exactly when the access token is most likely to have
// expired AND when Wi-Fi/VPN is most likely still reconnecting -- without
// this distinction, that blip looked identical to an actually-invalid
// refresh token and forced a logout for no real reason.
class RefreshUnavailableError extends Error {}

async function refreshToken(): Promise<string | null> {
  const refreshTok = localStorage.getItem('refresh_token');
  if (!refreshTok) return null;
  let res: Response;
  try {
    res = await fetch(`${API_URL}/api/v1/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: refreshTok }),
    });
  } catch {
    throw new RefreshUnavailableError('Could not reach the refresh endpoint');
  }
  if (!res.ok) return null;
  const data = await res.json();
  const newToken: string | undefined = data?.access_token;
  if (!newToken) return null;
  localStorage.setItem('access_token', newToken);
  if (localStorage.getItem('user_type') === 'super_admin') {
    localStorage.setItem('super_admin_token', newToken);
  }
  return newToken;
}

async function fetchWithRefresh(url: string, init?: RequestInit): Promise<Response> {
  let res = await fetch(url, { ...init, headers: { ...authHeaders(), ...(init?.headers as Record<string, string> || {}) } });
  if (res.status === 401) {
    let newToken: string | null = null;
    try {
      newToken = await refreshToken();
    } catch (err) {
      if (err instanceof RefreshUnavailableError) {
        // Couldn't even attempt the refresh -- leave the session alone
        // and just surface this one request's failure.
        return res;
      }
      throw err;
    }
    if (newToken) {
      res = await fetch(url, { ...init, headers: { ...authHeaders(), ...(init?.headers as Record<string, string> || {}) } });
    }
    if (res.status === 401) {
      const isSuperAdmin = window.location.pathname.startsWith('/super-admin');
      window.location.href = isSuperAdmin ? '/super-admin/login' : '/login';
    }
  }
  return res;
}

function humanSize(b: number | null): string {
  if (!b) return '';
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
}

function fmtTime(iso: string | null): string {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('en-IN', {
      day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
}

export interface DocumentRowProps {
  doc: DocDef;
  jobId: string;
  existing: DocRecord | undefined;
  extraction: ExtractionSummary | undefined;
  onUploaded: (rec: DocRecord) => void;
  onDeleted: (id: string) => void;
  onView: (rec: DocRecord) => void;
  mode?: 'staff' | 'portal';
  context?: RequestContext;
  // Claim Office role: view-only access to Documents — no Upload, Replace,
  // or Del, just View/Review. Enforced again server-side
  // (require_claim_write_access in claim_share_auth.py), this is UX only.
  readOnly?: boolean;
  // Garage delete-window restriction: Del hidden once the claim has moved
  // past its initial intake/review phase (Upload/Replace stay available —
  // not gated by this prop). Enforced again server-side
  // (require_claim_delete_access in claim_share_auth.py), this is UX only.
  deleteDisabled?: boolean;
}

export function DocumentRow({
  doc,
  jobId,
  existing,
  extraction,
  onUploaded,
  onDeleted,
  onView,
  mode = 'staff',
  context,
  readOnly = false,
  deleteDisabled = false,
}: DocumentRowProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const docsBase = context ? `${context.baseUrl}/${jobId}/documents` : DOCS_BASE(jobId);
  const [uploading, setUploading]         = useState(false);
  const [deleting, setDeleting]           = useState(false);
  const [checking, setChecking]           = useState(false);
  const [error, setError]                 = useState<string | null>(null);
  const [qualityResult, setQualityResult] = useState<QualityResult | null>(null);
  const [pendingFile, setPendingFile]     = useState<File | null>(null);

  async function runQualityCheck(_f: File): Promise<QualityResult | null> {
    // Disabled on request: the Gemini quality-assessment call (ai-service's
    // /check-quality, which used the advisor-ai-copilot-sa.json credential) cost
    // real billing on every file selected, good or bad. The upload flow was
    // already fail-open for this check (returning null here always falls through
    // to "upload immediately", same as every network/API error already did), so
    // skipping the call outright changes nothing about upload behavior -- it just
    // removes the poor-quality warning banner and the Gemini spend behind it.
    return null;
  }

  async function doUpload(f: File) {
    setError(null);
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', f);
      fd.append('doc_type', doc.key);

      const res = context
        ? await fetch(docsBase, { method: 'POST', headers: context.headers, body: fd })
        : await fetchWithRefresh(docsBase, { method: 'POST', body: fd });

      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body?.error?.message || body?.detail || `Upload failed (${res.status})`);
      }

      const rec: DocRecord = await res.json();
      setPendingFile(null);
      setQualityResult(null);
      onUploaded(rec);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Upload failed');
    } finally {
      setUploading(false);
    }
  }

  async function handleFile(f: File) {
    setError(null);
    setQualityResult(null);
    setPendingFile(null);
    setChecking(true);

    const quality = await runQualityCheck(f);
    setChecking(false);

    if (quality && quality.quality === 'poor') {
      // Hold the file and show the quality warning — user decides whether to re-upload or proceed
      setPendingFile(f);
      setQualityResult(quality);
      return;
    }

    // good / acceptable: upload immediately
    await doUpload(f);
  }

  async function handleDelete() {
    if (!existing) return;
    setDeleting(true);
    try {
      const deleteUrl = `${docsBase}/${existing.id}`;
      const res = context
        ? await fetch(deleteUrl, { method: HTTP_DELETE, headers: context.headers })
        : await fetchWithRefresh(deleteUrl, { method: HTTP_DELETE });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body?.error?.message || body?.detail || `Delete failed (${res.status})`);
      }
      onDeleted(existing.id);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Delete failed');
    } finally {
      setDeleting(false);
    }
  }

  const isUploaded = !!existing;
  const isExtractionEligible = EXTRACTION_DOC_TYPES.has(doc.key);
  const isPoorPending = qualityResult?.quality === 'poor' && !!pendingFile;

  // Status tag — derived from existing/uploading state, no new business
  // states beyond what DocSlot already tracked.
  let statusLabel: string;
  let statusClass: string;
  if (checking) {
    statusLabel = 'Processing';
    statusClass = 'text-blue-700 bg-blue-50 border-blue-200';
  } else if (isPoorPending) {
    statusLabel = 'Needs review';
    statusClass = 'text-orange-700 bg-orange-50 border-orange-200';
  } else if (isUploaded) {
    statusLabel = 'Uploaded';
    statusClass = 'text-green-700 bg-green-50 border-green-200';
  } else {
    statusLabel = 'Pending';
    statusClass = 'text-gray-500 bg-gray-50 border-gray-200';
  }

  // Background OCR status — when the extraction summary has a non-null
  // ocrStatus, it takes precedence over the upload-state-derived tag above
  // (design: adjuster must see Pending/Processing/Extracted/Failed without
  // opening the document). ocrStatus is null for non-eligible doc types or
  // when no OCR job has been created yet, in which case the existing
  // upload-state tag (checking/isPoorPending/isUploaded/Pending) is used
  // unchanged.
  const ocrStatus = extraction?.ocrStatus ?? null;
  let ocrBadgeTitle: string | undefined;
  if (ocrStatus === 'pending') {
    statusLabel = 'Pending';
    statusClass = 'text-gray-500 bg-gray-50 border-gray-200';
  } else if (ocrStatus === 'processing') {
    statusLabel = 'Processing';
    statusClass = 'text-blue-700 bg-blue-50 border-blue-200';
  } else if (ocrStatus === 'done') {
    statusLabel = 'Extracted';
    statusClass = 'text-green-700 bg-green-50 border-green-200';
  } else if (ocrStatus === 'failed') {
    statusLabel = 'Failed';
    statusClass = 'text-red-700 bg-red-50 border-red-200';
    ocrBadgeTitle = extraction?.ocrLastError ?? undefined;
  }

  const confidenceDisplay = extraction?.confidence != null ? `${extraction.confidence}` : '—';

  return (
    <div
      className={cn(
        'flex flex-col gap-1.5 border-b border-gray-100 px-3 py-1.5 text-sm last:border-b-0',
        'sm:grid sm:grid-cols-[minmax(0,2fr)_auto_auto_minmax(0,1.5fr)_auto] sm:items-center sm:gap-3',
      )}
    >
      {/* Label */}
      <div className="min-w-0">
        <p className="truncate font-medium text-gray-800">
          {doc.label}
          {doc.required && <span className="ml-1 text-xs text-red-500">*</span>}
        </p>
        {doc.hint && <p className="text-xs text-gray-400">{doc.hint}</p>}
      </div>

      {/* Status tag */}
      <div>
        <span
          title={ocrBadgeTitle}
          className={cn('inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium', statusClass)}
        >
          {statusLabel}
        </span>
      </div>

      {/* Confidence */}
      <div className="text-xs text-gray-500">
        <span className="sm:hidden font-medium text-gray-400 mr-1">Confidence:</span>
        {confidenceDisplay}
      </div>

      {/* File info */}
      <div className="min-w-0 text-xs text-gray-500">
        {existing ? (
          <>
            <p className="truncate">{existing.file_name}</p>
            <p className="text-gray-400">{humanSize(existing.file_size)} · {fmtTime(existing.uploaded_at)}</p>
          </>
        ) : (
          <span className="text-gray-300">No file uploaded</span>
        )}
      </div>

      {/* Actions */}
      <div className="flex flex-shrink-0 items-center gap-1.5">
        {isUploaded && existing ? (
          <>
            {isExtractionEligible ? (
              <button
                onClick={() => onView(existing)}
                className="rounded-md bg-violet-600 px-2.5 py-1 text-xs font-semibold text-white hover:bg-violet-700"
              >
                Review
              </button>
            ) : (
              <button
                onClick={() => onView(existing)}
                className="rounded-md border border-primary-300 bg-primary-50 px-2.5 py-1 text-xs font-medium text-primary-700 hover:bg-primary-100"
              >
                View
              </button>
            )}
            {!readOnly && mode === 'portal' && (
              <button
                onClick={() => cameraInputRef.current?.click()}
                className="rounded-md border border-primary-300 bg-primary-50 px-2.5 py-1 text-xs font-medium text-primary-600 hover:bg-primary-100"
              >
                Retake
              </button>
            )}
            {!readOnly && (
              <button
                onClick={() => inputRef.current?.click()}
                className="rounded-md border border-gray-300 bg-white px-2.5 py-1 text-xs font-medium text-gray-600 hover:bg-gray-50"
              >
                Replace
              </button>
            )}
            {!readOnly && !deleteDisabled && (
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="rounded-md border border-red-200 bg-white px-2.5 py-1 text-xs font-medium text-red-600 hover:bg-red-50 disabled:opacity-50"
              >
                {deleting ? '…' : 'Del'}
              </button>
            )}
          </>
        ) : checking ? (
          <span className="flex items-center gap-1.5 text-xs text-blue-600">
            <span className="h-3 w-3 animate-spin rounded-full border-b-2 border-blue-500" />
            Checking…
          </span>
        ) : isPoorPending ? (
          <span className="text-xs text-orange-600">See details below</span>
        ) : readOnly ? null : (
          <>
            {mode === 'portal' && (
              <button
                onClick={() => cameraInputRef.current?.click()}
                disabled={uploading}
                className="rounded-md border border-dashed border-primary-300 bg-primary-50 px-2.5 py-1 text-xs text-primary-600 hover:border-primary-400 hover:bg-primary-100 disabled:opacity-50"
              >
                {uploading ? 'Uploading…' : 'Take Photo'}
              </button>
            )}
            <button
              onClick={() => inputRef.current?.click()}
              disabled={uploading}
              className="rounded-md border border-dashed border-gray-300 bg-gray-50 px-2.5 py-1 text-xs text-gray-500 hover:border-primary-400 hover:bg-primary-50 hover:text-primary-600 disabled:opacity-50"
            >
              {uploading ? 'Uploading…' : 'Upload'}
            </button>
          </>
        )}
      </div>

      {error && (
        <p className="sm:col-span-5 text-xs text-red-500">{error}</p>
      )}

      {/* Quality-warning expandable sub-row (not a popover) — inline,
          directly beneath the row, per design Section 6.6. */}
      {isPoorPending && qualityResult && pendingFile && (
        <div className="sm:col-span-5 rounded-md border border-orange-300 bg-orange-50 px-3 py-2.5">
          <div className="flex items-start gap-2">
            <svg className="h-4 w-4 text-orange-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
            </svg>
            <div className="min-w-0 flex-1">
              <p className="text-xs font-semibold text-orange-700">
                Poor document quality (score {qualityResult.score}/100)
              </p>
              {qualityResult.issues.length > 0 && (
                <ul className="mt-1 space-y-0.5">
                  {qualityResult.issues.map((issue, i) => (
                    <li key={i} className="text-xs text-orange-600">· {issue}</li>
                  ))}
                </ul>
              )}
              {qualityResult.suggestion && (
                <p className="mt-1.5 text-xs text-orange-600 italic">{qualityResult.suggestion}</p>
              )}
              <div className="mt-2 flex gap-2">
                <button
                  onClick={() => { setQualityResult(null); setPendingFile(null); inputRef.current?.click(); }}
                  className="rounded-md border border-orange-400 bg-white px-3 py-1.5 text-xs font-medium text-orange-700 hover:bg-orange-50"
                >
                  Re-upload
                </button>
                <button
                  onClick={() => doUpload(pendingFile)}
                  disabled={uploading}
                  className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-50 disabled:opacity-50"
                >
                  {uploading ? 'Uploading…' : 'Upload Anyway'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {mode === 'portal' && (
        <input
          ref={cameraInputRef}
          data-testid={`file-input-camera-${doc.key}`}
          type="file"
          accept="image/*"
          capture="environment"
          className="hidden"
          onChange={e => { const f = e.target.files?.[0]; if (f) handleFile(f); e.target.value = ''; }}
        />
      )}
      <input
        ref={inputRef}
        data-testid={`file-input-${doc.key}`}
        type="file"
        accept="image/*,.pdf,.doc,.docx,.xls,.xlsx"
        className="hidden"
        onChange={e => { const f = e.target.files?.[0]; if (f) handleFile(f); e.target.value = ''; }}
      />
    </div>
  );
}
